import { z } from "zod";
import { paginationQuery } from "../pagination";

/**
 * TSR service tokens (0058): credentials for things that are not a person —
 * venue screens, Stream Decks, the shooting ingest, later the venue server.
 * All three ways in are `sessions.kind = SERVICE`, so revocation, the Redis
 * denylist and refresh rotation are the user sessions' own machinery:
 *
 *   DELEGATED           a signed-in TSR user's display window
 *                       (`POST /tsr/display-tokens`); dies with that user's session.
 *   PAIRED              a standalone screen shows a code, an admin approves it
 *                       (`/tsr/devices/pair…`).
 *   CLIENT_CREDENTIALS  a machine client with `client_id` + secret
 *                       (`POST /auth/service-token`).
 *
 * A device's scope — kind, sport, meet, station — never changes: revoke it and
 * make another (like an access rule's key). That is what lets the scope ride in
 * the access token with no query per request.
 */

export const TSR_DEVICE_KINDS = ["DISPLAY", "STREAM_DECK", "INGEST", "VENUE_SERVER"] as const;
export type TsrDeviceKind = (typeof TSR_DEVICE_KINDS)[number];

export const TSR_DEVICE_AUTH_METHODS = ["DELEGATED", "PAIRED", "CLIENT_CREDENTIALS"] as const;
export type TsrDeviceAuthMethod = (typeof TSR_DEVICE_AUTH_METHODS)[number];

export const TSR_DEVICE_STATUSES = ["ACTIVE", "REVOKED"] as const;
export type TsrDeviceStatus = (typeof TSR_DEVICE_STATUSES)[number];

/** `tsr_device_pairings.status`. Expiry itself is `expires_at`; EXPIRED is the sweep's stamp. */
export const TSR_PAIRING_STATUSES = ["PENDING", "APPROVED", "CLAIMED", "EXPIRED"] as const;
export type TsrPairingStatus = (typeof TSR_PAIRING_STATUSES)[number];

export const SERVICE_SCOPES = ["read", "write"] as const;
export type ServiceScope = (typeof SERVICE_SCOPES)[number];

/**
 * What a kind may do, decided by the kind and never by the request. `write`
 * only opens routes that opt in to service tokens AND ask for write; it never
 * stands in for a user group (no existing write route accepts a service token).
 */
export const TSR_DEVICE_SCOPE = {
	DISPLAY: "read",
	STREAM_DECK: "write",
	INGEST: "write",
	VENUE_SERVER: "write",
} as const satisfies Record<TsrDeviceKind, ServiceScope>;

/** Delegated display tokens are always a read-only DISPLAY. */
export const TSR_DELEGATED_DEVICE_KIND = "DISPLAY" satisfies TsrDeviceKind;

/** How long a delegated display token can be refreshed, at most (and never past the issuing session). */
export const TSR_DISPLAY_TOKEN_TTL_SECONDS = 12 * 3600;
/** A pairing code is good for this long; an approved pairing waits this long again for its device. */
export const TSR_PAIRING_TTL_SECONDS = 600;
/** How often a pairing device should poll. */
export const TSR_PAIRING_POLL_SECONDS = 3;
/** Pairing codes: 6 characters, no 0 / O / 1 / I (read off a screen across a hall). */
export const TSR_PAIRING_CODE_LENGTH = 6;
export const TSR_PAIRING_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const TSR_PAIRING_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;

export const TSR_DEVICE_NAME_MAX = 120;
export const TSR_STATION_ID_MAX = 64;
export const TSR_STATION_ID_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/;

// ── Wire ────────────────────────────────────────────────────────────────────

/** Every service-token answer. The access token lives `expires_in` seconds; refresh with `POST /auth/service-token/refresh`. */
export interface ServiceTokenPair {
	access_token: string;
	token_type: "Bearer";
	expires_in: number;
	refresh_token: string;
	refresh_expires_at: string;
	scope: ServiceScope;
}

/** What a service token is, as `GET /tsr/service/me` (and the socket identity) carry it. */
export interface ServiceIdentityView {
	kind: "SERVICE";
	tsr_device_id: string;
	device_kind: TsrDeviceKind;
	auth_method: TsrDeviceAuthMethod;
	sport_id: string;
	meet_id: string | null;
	station_id: string | null;
	scope: ServiceScope;
	/** DELEGATED only: the user whose session issued it. */
	issued_by_user_id: string | null;
}

export interface TsrDevice {
	tsr_device_id: string;
	name: string;
	kind: TsrDeviceKind;
	auth_method: TsrDeviceAuthMethod;
	scope: ServiceScope;
	sport: { sport_id: string; code: string; name: string };
	meet_id: string | null;
	station_id: string | null;
	client_id: string | null;
	status: TsrDeviceStatus;
	/** Live service sessions (signed in now). */
	live_sessions: number;
	created_by: { id: string; username: string } | null;
	created_at: string;
	last_used_at: string | null;
	revoked_at: string | null;
}

/** `POST /tsr/devices` and `/:id/rotate-secret`. `client_secret` is shown ONCE; only its argon2 hash is kept. */
export interface TsrDeviceSecret {
	device: TsrDevice;
	client_id: string;
	client_secret: string;
}

export interface TsrDisplayToken extends ServiceTokenPair {
	/** The device row the token is filed under (revoke it with `DELETE /tsr/display-tokens/:id`). */
	tsr_device_id: string;
}

export interface TsrPairingStart {
	pairing_id: string;
	code: string;
	expires_at: string;
	poll_interval_seconds: number;
}

/** `GET /tsr/devices/pair/:pairing_id`. The tokens come ONCE; the next poll answers `expired`. */
export type TsrPairingPoll =
	| { status: "pending"; expires_at: string }
	| { status: "expired" }
	| { status: "approved"; device: ServiceIdentityView & { name: string }; tokens: ServiceTokenPair };

const stationId = z.string().trim().regex(TSR_STATION_ID_PATTERN, "Letters, digits and . _ : - only (at most 64)");
const deviceName = z.string().trim().min(1).max(TSR_DEVICE_NAME_MAX);

/** `POST /tsr/display-tokens`. */
export const issueDisplayTokenBody = z.object({
	sport_id: z.string().uuid(),
	meet_id: z.string().uuid().optional(),
	station_id: stationId.optional(),
});

/** What a device is for: the scope it will carry for life. */
export const tsrDeviceScopeBody = z.object({
	name: deviceName,
	kind: z.enum(TSR_DEVICE_KINDS),
	sport_id: z.string().uuid(),
	meet_id: z.string().uuid().optional(),
	station_id: stationId.optional(),
});

/** `POST /tsr/devices/pair/approve`. The code is case-blind. */
export const approvePairingBody = tsrDeviceScopeBody.extend({
	code: z.string().trim().toUpperCase().regex(TSR_PAIRING_CODE_PATTERN, "A 6-character pairing code"),
});

/** `POST /auth/service-token`. */
export const serviceTokenBody = z.object({
	client_id: z.string().trim().min(1).max(100),
	client_secret: z.string().min(1).max(200),
});

export const listTsrDevicesQuery = paginationQuery.extend({
	sport_id: z.string().uuid().optional(),
	kind: z.enum(TSR_DEVICE_KINDS).optional(),
	auth_method: z.enum(TSR_DEVICE_AUTH_METHODS).optional(),
	status: z.enum(TSR_DEVICE_STATUSES).optional(),
});
export type ListTsrDevicesQuery = z.infer<typeof listTsrDevicesQuery>;
